import { useContext, useState } from 'react';
import { ClientFavicon, SectionHeading, Swiper } from '../../Components';
import ThemeContext from '../../contexts/ThemeContext';
import './Portfolio.css';

const Portfolio = ({ language, font }) => {
  const { p, h2, clients, types, categories, viewProject, videoTypes, promoSubcategories } = language.portfolio;
  const [activeCategory, setActiveCategory] = useState('websites');
  const [filter, setFilter] = useState('store');
  const [subcategoryFilter, setSubcategoryFilter] = useState(null);

  // Function to extract YouTube video ID and generate thumbnail URL
  const getYouTubeThumbnail = (videoUrl) => {
    if (!videoUrl || !videoUrl.includes('youtube.com')) {
      return null;
    }
    
    // Extract video ID from various YouTube URL formats
    let videoId = null;
    if (videoUrl.includes('/shorts/')) {
      videoId = videoUrl.split('/shorts/')[1]?.split('?')[0];
    } else if (videoUrl.includes('watch?v=')) {
      videoId = videoUrl.split('watch?v=')[1]?.split('&')[0];
    }
    
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }
    
    return null;
  };
  const { theme } = useContext(ThemeContext);

  const getFilteredItems = () => {
    const categoryItems = clients[activeCategory] || [];
    let filteredItems = filter ? categoryItems.filter((item) => item.type === filter) : categoryItems;
    
    // Apply subcategory filter for promo videos
    if (filter === 'promo' && subcategoryFilter && subcategoryFilter !== 'الكل' && subcategoryFilter !== 'All' && subcategoryFilter !== 'הכל') {
      filteredItems = filteredItems.filter((item) => item.subcategory === subcategoryFilter);
    }
    
    return filteredItems;
  };

  const getAvailableVideoTypes = () => {
    const videoItems = clients.videos || [];
    const availableTypes = new Set();
    
    videoItems.forEach(item => {
      if (item.type) {
        availableTypes.add(item.type);
      }
    });
    
    return Array.from(availableTypes);
  };

  const handleCategoryClick = (category) => {
    setActiveCategory(category);
    setSubcategoryFilter(null); // Reset subcategory filter
    // Set all as default filter for videos category
    if (category === 'videos') {
      setFilter(null);
    } else {
      setFilter(null);
    }
  };

  const handleFilterClick = (type) => {
    setFilter(type);
    setSubcategoryFilter(null); // Reset subcategory filter when changing main filter
  };

  const handleSubcategoryClick = (subcategory) => {
    setSubcategoryFilter(subcategory);
  };

  const renderPortfolioItem = (item, index) => {
    const isWebsites = activeCategory === 'websites';
    
    if (isWebsites) {
      // For websites: Show favicon grid (your current approach)
      return (
        <div key={index} className="portfolio-favicon-item">
          <ClientFavicon
            name={item.name}
            website={item.website || item.link}
            favicon={item.favicon}
          />
        </div>
      );
    } else {
      // For other categories: Show modern cards with previews
      const isVideo = activeCategory === 'videos';
      const isBranding = activeCategory === 'branding';
      const thumbnailUrl = isVideo && item.videoUrl ? getYouTubeThumbnail(item.videoUrl) : (item.preview || item.favicon);
      
      return (
        <div key={index} className={`portfolio-card ${activeCategory}`}>
          {isVideo && item.videoUrl ? (
            <a href={item.videoUrl} target="_blank" rel="noreferrer" className="video-card-link">
              <div className="card-preview">
                <img 
                  src={thumbnailUrl || item.preview || item.favicon} 
                  alt={item.name} 
                  onError={(e) => {
                    // Fallback to preview image if YouTube thumbnail fails
                    if (thumbnailUrl && thumbnailUrl.includes('youtube.com')) {
                      e.target.src = item.preview || item.favicon;
                    }
                  }}
                />
                <div className="play-button-overlay">
                  <div className="play-button">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                </div>
                <div className="card-overlay">
                  <div className="card-info">
                    <h4>{item.name}</h4>
                    <p>{item.description}</p>
                    <span className="view-project">{viewProject}</span>
                  </div>
                </div>
              </div>
            </a>
          ) : (
            <>
              <div className="card-preview">
                <img 
                  src={thumbnailUrl || item.preview || item.favicon} 
                  alt={item.name} 
                  onError={(e) => {
                    // Fallback to preview image if YouTube thumbnail fails
                    if (thumbnailUrl && thumbnailUrl.includes('youtube.com')) {
                      e.target.src = item.preview || item.favicon;
                    }
                  }}
                />
                <div className="card-overlay">
                  <div className="card-info">
                    <h4>{item.name}</h4>
                    <p>{item.description}</p>
                    {item.link && (
                      <a href={item.link} target="_blank" rel="noreferrer" className="view-project">
                        {viewProject}
                      </a>
                    )}
                  </div>
                </div>
              </div>
              {/* Only show card-favicon for non-branding categories */}
              {!isBranding && (
                <div className="card-favicon">
                  <ClientFavicon
                    name={item.name}
                    website={item.website || item.link}
                    favicon={item.favicon}
                  />
                </div>
              )}
            </>
          )}
        </div>
      );
    }
  };

  return (
    <div className='Portfolio flex-center section' id={language.menu[3][1]} style={{ direction: 'rtl' }}>
      <SectionHeading p={p} h2={h2} />
      
      {/* Category Navigation */}
      <div className={`portfolio-categories ${font}`}>
        {categories.map((category) => (
          <button
            key={category.key}
            onClick={() => handleCategoryClick(category.key)}
            className={`category-btn ${activeCategory === category.key ? 'active' : ''} ${font}`}
            style={{ color: theme.text }}
          >
            <div className="category-icon">
              {category.icon}
            </div>
            <span>{category.name}</span>
          </button>
        ))}
      </div>

      {/* Type Filters - Only for Websites */}
      {activeCategory === 'websites' && (
        <div className={`portfolio-filters ${font}`}>
          <button onClick={() => handleFilterClick(null)} className={filter === null ? `active ${font}` : `${font}`}>
            {types[0]}
          </button>
          <button onClick={() => handleFilterClick('store')} className={filter === 'store' ? `active ${font}` : `${font}`}>
            {types[2]}
          </button>
          <button onClick={() => handleFilterClick('menu')} className={filter === 'menu' ? `active ${font}` : `${font}`}>
            {types[1]}
          </button>
          <button onClick={() => handleFilterClick('svc-web')} className={filter === 'svc-web' ? `active ${font}` : `${font}`}>
            {types[3]}
          </button>
        </div>
      )}

      {/* Video Type Filters - Only for Videos */}
      {activeCategory === 'videos' && (
        <div className={`portfolio-filters ${font}`}>
          <button onClick={() => handleFilterClick(null)} className={filter === null ? `active ${font}` : `${font}`}>
            {videoTypes[0]}
          </button>
          {getAvailableVideoTypes().includes('promo') && (
            <button onClick={() => handleFilterClick('promo')} className={filter === 'promo' ? `active ${font}` : `${font}`}>
              {videoTypes[1]}
            </button>
          )}
          {getAvailableVideoTypes().includes('motion-vfx') && (
            <button onClick={() => handleFilterClick('motion-vfx')} className={filter === 'motion-vfx' ? `active ${font}` : `${font}`}>
              {videoTypes[2]}
            </button>
          )}
          {getAvailableVideoTypes().includes('trends') && (
            <button onClick={() => handleFilterClick('trends')} className={filter === 'trends' ? `active ${font}` : `${font}`}>
              {videoTypes[3]}
            </button>
          )}
          {getAvailableVideoTypes().includes('explainer') && (
            <button onClick={() => handleFilterClick('explainer')} className={filter === 'explainer' ? `active ${font}` : `${font}`}>
              {videoTypes[4]}
            </button>
          )}
          {getAvailableVideoTypes().includes('content') && (
            <button onClick={() => handleFilterClick('content')} className={filter === 'content' ? `active ${font}` : `${font}`}>
              {videoTypes[5]}
            </button>
          )}
        </div>
      )}

      {/* Promo Subcategory Filters - Only when Promo is selected */}
      {activeCategory === 'videos' && filter === 'promo' && (
        <div className={`portfolio-subcategory-filters ${font}`}>
          {promoSubcategories && promoSubcategories.map((subcategory, index) => (
            <button 
              key={index}
              onClick={() => handleSubcategoryClick(subcategory)} 
              className={subcategoryFilter === subcategory ? `active ${font}` : `${font}`}
              style={{
                color: theme.mode === 'light' ? '#303192' : 'white',
                backgroundColor: theme.mode === 'light' ? '#f5f5f5' : 'rgba(255, 255, 255, 0.1)',
                ...(subcategoryFilter === subcategory && theme.mode === 'light' ? {
                  backgroundColor: 'rgba(244, 122, 26, 0.2)'
                } : {})
              }}
            >
              {subcategory}
            </button>
          ))}
        </div>
      )}

      {/* Portfolio Display */}
      <div className={`portfolio-display ${activeCategory}`}>
        {activeCategory === 'websites' ? (
          // Websites: Favicon Grid (your current approach)
          <div className="portfolio-favicon-grid">
            {getFilteredItems().map((item, i) => renderPortfolioItem(item, i))}
          </div>
        ) : (
          // Other categories: Modern Cards
          <div className="portfolio-cards-grid">
            {getFilteredItems().map((item, i) => renderPortfolioItem(item, i))}
          </div>
        )}
      </div>

      {/* Enhanced Slider for larger previews - Only for Websites */}
      {activeCategory === 'websites' && (
        <div className="portfolio-slider" style={{ backgroundColor: theme.cardsBg }} id='4'>
          <Swiper slides={getFilteredItems()} />
        </div>
      )}
    </div>
  );
};

export default Portfolio; 